import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Rocket, Users, Zap } from 'lucide-react';

const SlideOutForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTrigger, setShowTrigger] = useState(false);

  // Show trigger button after user has been on page for 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTrigger(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  // Also show on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500 && !showTrigger) {
        setShowTrigger(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showTrigger]);

  const handleSubmit = (e) => {
    // Don't prevent default - let the form submit naturally to bypass CORS
    setIsSubmitting(true);
    
    // Show success message and close form after a delay
    setTimeout(() => {
      setIsOpen(false);
      setIsSubmitting(false);
      alert('🎉 Welcome to the Aquargin family! We\'ll be in touch faster than you can say "automation"!');
    }, 1500);
  };

  const triggerVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -180 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      rotate: 0,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 20,
        delay: 0.5
      }
    }
  };

  const formVariants = {
    hidden: { x: '100%', opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 30 
      }
    },
    exit: { 
      x: '100%', 
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <>
      {/* Hidden iframe for form submission */}
      <iframe 
        name="aquargin-form-frame" 
        style={{ display: 'none' }}
        title="Form submission frame"
      ></iframe>
      
      {/* Floating Trigger Button */}
      <AnimatePresence>
        {showTrigger && !isOpen && (
          <motion.button
            variants={triggerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={() => setIsOpen(true)}
            className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-primary-500 to-secondary-500 text-white p-4 rounded-full shadow-2xl hover:shadow-xl transition-all duration-300 group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center space-x-2">
              <Sparkles className="h-6 w-6 animate-pulse" />
              <span className="hidden sm:block font-semibold">
                Ready to Automate? 🚀
              </span>
            </div>
            
            {/* Notification Badge */}
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center animate-bounce">
              !
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Slide-out Form */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Form Panel */}
            <motion.div
              variants={formVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <div className="relative h-full flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-6 text-white relative overflow-hidden">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  
                  <div className="relative z-10">
                    <div className="flex items-center space-x-2 mb-2">
                      <Rocket className="h-6 w-6" />
                      <h2 className="text-2xl font-bold">
                        Ready to Join the Automation Revolution?
                      </h2>
                    </div>
                    <p className="text-lg opacity-90">
                      🎯 Let's turn your business into an efficiency machine!
                    </p>
                  </div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full translate-y-12 -translate-x-12"></div>
                </div>

                {/* Form Content */}
                <div className="flex-1 p-6 bg-gray-50">
                  <div className="mb-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      🌟 Why Companies Choose Aquargin
                    </h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-2xl mb-1">⚡</div>
                        <div className="font-semibold text-primary">75% Faster</div>
                        <div className="text-gray-600">Processes</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl mb-1">💰</div>
                        <div className="font-semibold text-green-600">60% Savings</div>
                        <div className="text-gray-600">Costs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl mb-1">🚀</div>
                        <div className="font-semibold text-purple-600">300% ROI</div>
                        <div className="text-gray-600">Average</div>
                      </div>
                    </div>
                  </div>

                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-4"
                    action="https://app.aquargin.com/Rest/web/d949b33238274124aa8a6954/c0010000000000000001001A"
                    method="POST"
                    target="aquargin-form-frame"
                  >
                    {/* Name Field */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Users className="h-4 w-4 mr-2 text-primary" />
                        What should we call you, future automation champion?
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="John Doe (or whatever your parents named you 😊)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    {/* Phone Field */}
                    <div>
                      <label htmlFor="phone__m" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Zap className="h-4 w-4 mr-2 text-green-500" />
                        Your hotline to automation success
                      </label>
                      <input
                        type="tel"
                        id="phone__m"
                        name="phone__m"
                        required
                        placeholder="+1 (555) 123-4567"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label htmlFor="email__m" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Send className="h-4 w-4 mr-2 text-blue-500" />
                        Where should we send the good news?
                      </label>
                      <input
                        type="email"
                        id="email__m"
                        name="email__m"
                        required
                        placeholder="you@yourcompany.com"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    {/* Company Field */}
                    <div>
                      <label htmlFor="company_name__m" className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Sparkles className="h-4 w-4 mr-2 text-purple-500" />
                        Which company are we about to revolutionize?
                      </label>
                      <input
                        type="text"
                        id="company_name__m"
                        name="company_name__m"
                        placeholder="Acme Corp (or your empire's name)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      />
                    </div>

                    {/* Message Field */}
                    <div>
                      <label htmlFor="message__m" className="block text-sm font-semibold text-gray-700 mb-2">
                        💬 Tell us your automation dreams (or current nightmares)
                      </label>
                      <textarea
                        id="message__m"
                        name="message__m"
                        rows="4"
                        placeholder="We want to automate our [process] because we spend [too much time] on [tedious task]..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-none"
                      />
                    </div>

                    {/* Trust Indicators */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-center text-sm text-gray-600">
                        <div className="flex items-center justify-center space-x-4 mb-2">
                          <span>🔒 Enterprise Security</span>
                          <span>⚡ 24/7 Support</span>
                          <span>🚀 30-Day Trial</span>
                        </div>
                        <p>Join Fortune 500 companies already saving millions with Aquargin</p>
                      </div>
                    </div>

                    {/* Highlighted Submit Button */}
                    <div className="relative">
                      {/* Glow effect */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-red-500 to-pink-500 rounded-lg blur opacity-30 animate-pulse"></div>
                      
                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.05, rotate: [0, -1, 1, 0] }}
                        whileTap={{ scale: 0.95 }}
                        className="relative w-full py-5 px-6 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white font-black text-lg rounded-lg shadow-2xl hover:shadow-yellow-500/25 transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:rotate-1"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center space-x-3">
                            <div className="animate-spin h-6 w-6 border-3 border-white border-t-transparent rounded-full"></div>
                            <span className="text-lg">🔥 Launching Your Success...</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center space-x-3">
                            <Rocket className="h-6 w-6 animate-bounce" />
                            <span className="text-lg font-black tracking-wide">
                              🚀 START MY AUTOMATION JOURNEY!
                            </span>
                            <Sparkles className="h-6 w-6 animate-spin" />
                          </div>
                        )}
                        
                        {/* Animated background elements */}
                        <div className="absolute inset-0 rounded-lg overflow-hidden">
                          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 -translate-x-full animate-shine"></div>
                        </div>
                      </motion.button>
                    </div>
                    
                    {/* Extra emphasis */}
                    <div className="text-center">
                      <p className="text-sm font-bold text-orange-600 animate-pulse">
                        ⚡ Join companies already automating! ⚡
                      </p>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you're agreeing to let us blow your mind with automation possibilities. 
                      No spam, just pure efficiency magic! ✨
                    </p>
                  </form>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default SlideOutForm; 